import { type ClassValue, clsx } from 'clsx';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import queryString from 'query-string';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toQueryString(url: string, query?: Record<string, unknown>): string {
  if (!query || Object.keys(query).length === 0) return url;

  const encodedQuery = Object.entries(query).reduce<Record<string, string>>((acc, [key, value]) => {
    if (typeof value === 'object' && value !== null) {
      acc[key] = JSON.stringify(value);
    } else {
      acc[key] = String(value);
    }
    return acc;
  }, {});

  const queryStr = queryString.stringify(encodedQuery, {
    encode: true, // кодувати спецсимволи
    skipNull: true, // пропускати null
    skipEmptyString: true, // пропускати ""
    sort: false, // залишати порядок полів, як у тебе в коді
  });

  return `${url}?${queryStr}`;
}

export function formatNumberCompact(value: number | string): string {
  const number = typeof value === 'string' ? Number(value) : value;
  return Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(number);
}

export function shortenString(str: string, prefixLength: number = 7): string {
  if (!str) return '';
  return str.length > prefixLength ? `${str.slice(0, prefixLength)}...` : str;
}

export function clearFormData<T>(obj: Record<string, any>): T {
  const clean = (value: any): any => {
    if (value === null || value === undefined || value === '' || value === 'none') {
      return undefined;
    }

    if (
      value instanceof File ||
      value instanceof Blob ||
      value instanceof Date ||
      value instanceof FileList
    ) {
      return value;
    }

    if (Array.isArray(value)) {
      const cleanedArray = value.map(clean).filter((v) => v !== undefined);

      return cleanedArray.length ? cleanedArray : undefined;
    }

    if (typeof value === 'object') {
      const result: Record<string, any> = {};

      Object.entries(value).forEach(([k, v]) => {
        const cleaned = clean(v);
        if (cleaned !== undefined) {
          result[k] = cleaned;
        }
      });

      return Object.keys(result).length ? result : undefined;
    }

    return value;
  };

  return clean(obj) as T;
}

export function formatDuration(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '—';

  const start = parseISO(startDate);
  const end = parseISO(endDate);

  const diffMs = differenceInMilliseconds(end, start);

  if (diffMs <= 0) return '0s';

  const seconds = Math.floor(diffMs / 1000) % 60;
  const minutes = Math.floor(diffMs / (1000 * 60)) % 60;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));

  const parts: string[] = [];

  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(' ');
}

export function isEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;

  if (typeof a !== 'object') return a === b;

  // Arrays
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => isEqual(item, b[i]));
  }

  // Objects
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(
    (key) => Object.prototype.hasOwnProperty.call(b, key) && isEqual(a[key], b[key]),
  );
}

export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result: T = { ...target };

  for (const key in source) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;

    const sourceValue = source[key];
    const targetValue = result[key];

    if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
      result[key] = deepMerge(
        targetValue as Record<string, any>,
        sourceValue as Record<string, any>,
      ) as T[typeof key];
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue as T[typeof key];
    }
  }

  return result;
}

function isPlainObject(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function formatUTCDate(
  dateString: string,
  formatStr: string = 'dd/MM/yyyy HH:mm:ss',
): string {
  const date = new Date(dateString);

  const tokens: Record<string, string> = {
    dd: String(date.getUTCDate()).padStart(2, '0'),
    MM: String(date.getUTCMonth() + 1).padStart(2, '0'),
    yyyy: String(date.getUTCFullYear()),
    HH: String(date.getUTCHours()).padStart(2, '0'),
    mm: String(date.getUTCMinutes()).padStart(2, '0'),
    ss: String(date.getUTCSeconds()).padStart(2, '0'),
  };

  return formatStr.replace(/dd|MM|yyyy|HH|mm|ss/g, (match) => tokens[match]);
}

export function toUTCDate(date: Date, time: 'start' | 'end'): Date {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  if (time === 'start') {
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  }
  return new Date(Date.UTC(year, month, day - 1, 23, 59, 59, 999));
}

export function downloadFile(
  response: { data: BlobPart; headers: Record<string, unknown> },
  defaultFileName: string = 'report.xlsx',
) {
  const contentType = response.headers['content-type'] as string | undefined;
  const contentDisposition = response.headers['content-disposition'] as string | undefined;

  const blob = new Blob([response.data], {
    type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const fileName = contentDisposition?.match(/filename="(.+)"/)?.[1] ?? defaultFileName;

  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(url);
}

export function copyToClipboard(
  text: string,
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {},
) {
  navigator.clipboard
    .writeText(text)
    .then(() => options.onSuccess?.())
    .catch(options.onError);
}
